import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { useCallback } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Modal } from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { HospitalityRoomStatus } from '../../constants/roomStatuses'
import { useHotelsI18n } from '../../i18n'
import { useUpdateRoomStatus } from '../../queries/hooks'
import { useRoomWakeupData } from '../../queries/roomWakeupData'
import type { EnrichedRoom, HospitalityPermissions } from '../../types/rooms'
import { getHotelsPlaceholderText } from '../../utils/display'
import {
  formatIsoInTimeZone,
  getWakeupInstantIso,
} from '../../utils/formatting'
import { RoomStatusBadge } from '../rooms/RoomStatusBadge'
import { RoomStatusButtons } from '../rooms/RoomStatusButtons'
import { WakeupStatusBadge } from '../wakeup/WakeupStatusBadge'

interface RoomDetailModalProps {
  room: EnrichedRoom
  open: boolean
  onClose: () => void
  api: HotelsApi
  pbxId: number
  pbxUuid: string
  pbxTimeZone: string
  toast: ToastLike
  permissions: HospitalityPermissions
}

export const RoomDetailModal = ({
  room,
  open,
  onClose,
  api,
  pbxId,
  pbxUuid,
  pbxTimeZone,
  toast,
  permissions,
}: RoomDetailModalProps) => {
  const { text, locale } = useHotelsI18n()
  const placeholder = getHotelsPlaceholderText(text)
  const updateStatus = useUpdateRoomStatus({
    api,
    pbxId,
    options: {
      onSuccess: () => {
        toast.create({
          title: text(
            'hotels.dashboard.roomDetail.statusUpdated',
            'Room status updated',
          ),
          type: 'success',
        })
        onClose()
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.dashboard.roomDetail.statusUpdateFailed',
            'Failed to update room status',
          ),
          type: 'error',
        })
      },
    },
  })

  const handleStatusChange = useCallback(
    (status: HospitalityRoomStatus) => {
      if (!permissions.canEditQueues) return
      updateStatus.mutate({ extensionUuid: room.extension_uuid, status })
    },
    [permissions.canEditQueues, room.extension_uuid, updateStatus],
  )

  const { wakeups: activeWakeups, isLoading: isWakeupsLoading } =
    useRoomWakeupData({
      api,
      pbxUuid,
      room,
      options: {
        enabled: open && !!pbxUuid,
      },
    })

  return (
    <Modal
      open={open}
      onOpenChange={(details) => {
        if (!details.open) onClose()
      }}
      title={text('hotels.dashboard.roomDetail.title', 'Room {{room}}', {
        room: room.number,
      })}
      maxWidth="2xl"
    >
      <VStack align="stretch" gap={6}>
        <HStack justify="space-between" w="full">
          {room.name && (
            <Text fontSize="sm" color="fg.muted">
              {room.name}
            </Text>
          )}
          <RoomStatusBadge status={room.status_code} />
        </HStack>
        <Box
          p={4}
          borderWidth={1}
          borderColor="border.default"
          borderRadius="xl"
          bg="bg.subtle"
        >
          <Text fontWeight="semibold" mb={3}>
            {text('hotels.dashboard.roomDetail.changeStatus', 'Change Status')}
          </Text>
          <RoomStatusButtons
            currentStatus={room.status_code}
            onStatusChange={handleStatusChange}
            isDisabled={updateStatus.isPending || !permissions.canEditQueues}
          />
          {room.status_code !== HospitalityRoomStatus.CLEAN &&
          room.status_code !== HospitalityRoomStatus.INSPECTED ? null : (
            <Text fontSize="xs" color="fg.muted" mt={2}>
              {text(
                'hotels.dashboard.roomDetail.cleanOrInspectedWarning',
                'Setting to Clean or Inspected will cancel active wake-up calls and clear voicemails.',
              )}
            </Text>
          )}
        </Box>

        <Box
          p={4}
          borderWidth={1}
          borderColor="border.default"
          borderRadius="xl"
          bg="bg.subtle"
        >
          <Text fontWeight="semibold" mb={3}>
            {text(
              'hotels.dashboard.roomDetail.scheduledWakeups',
              'Scheduled Wake-Up Calls',
            )}
          </Text>
          {!pbxUuid ? (
            <Text fontSize="sm" color="fg.muted">
              {text(
                'hotels.dashboard.roomDetail.wakeupsUnavailable',
                'Wake-up details are unavailable for this PBX.',
              )}
            </Text>
          ) : isWakeupsLoading ? (
            <Text fontSize="sm" color="fg.muted">
              {text('hotels.dashboard.wakeupPopover.loading', 'Loading...')}
            </Text>
          ) : activeWakeups.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              {text(
                'hotels.dashboard.wakeupPopover.none',
                'No active wake-up calls',
              )}
            </Text>
          ) : (
            <VStack align="stretch" gap={2}>
              {activeWakeups.map((wc) => (
                <HStack key={wc.id} justify="space-between">
                  <Text fontSize="sm">
                    {getWakeupInstantIso(wc)
                      ? formatIsoInTimeZone(
                          getWakeupInstantIso(wc),
                          locale,
                          pbxTimeZone,
                        )
                      : placeholder}
                  </Text>
                  <WakeupStatusBadge
                    displayStatus={wc.display_status ?? 'scheduled'}
                  />
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </Modal>
  )
}
